import React, { useEffect, useState } from 'react';
import './Services.css';
import { apiGet, apiPost } from '../api';

const Services = ({ language }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');
  const [services, setServices] = useState([]);
  const [page, setPage] = useState({ heading: '', sub: '' });

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const data = await apiGet('/api/services', { locale: language, sort: 'order:asc' });
        if (!ignore && Array.isArray(data)) {
          const mapped = data.map((s, i) => ({
            id: s.id || i + 1,
            title: s.title || s.translations?.find?.((t) => t.locale === language)?.title,
            description: s.description || s.translations?.find?.((t) => t.locale === language)?.description,
            details: s.details || s.translations?.find?.((t) => t.locale === language)?.details,
            link: s.link,
            phone: s.phone || null,
            email: s.email || null,
            slug: s.slug || ((s.title || s.translations?.find?.((t) => t.locale === language)?.title || '')
              .toLowerCase()
              .normalize('NFKD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/(^-|-$)+/g, '')),
          }));
          setServices(mapped.filter((m) => m.title));
        }
      } catch (_) {}
    })();
    return () => { ignore = true; };
  }, [language]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const sp = await apiGet('/api/services-page', { locale: language });
        if (!ignore && sp) setPage({ heading: sp.heading || '', sub: sp.sub || '' });
      } catch (_) {}
    })();
    return () => { ignore = true; };
  }, [language]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMsg('');
    setSubmitting(true);
    try {
      await apiPost('/api/contact-requests', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      setSubmitMsg(language === 'en' ? 'Thanks! We will contact you soon.' : 'നന്ദി! ഞങ്ങൾ ഉടൻ ബന്ധപ്പെടും.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setSubmitMsg(language === 'en' ? 'Submission failed. Please try again.' : 'അയയ്ക്കൽ പരാജയപ്പെട്ടു. വീണ്ടും ശ്രമിക്കുക.');
    } finally {
      setSubmitting(false);
    }
  };

  const content = {
    en: {
      heading: 'Our Services',
      subheading: 'Agroecology Education, Training and Consultation',
      services: [
        {
          id: 1,
          title: 'Online Courses',
          description: 'We offer interactive online courses covering various aspects of sustainable agriculture, including Purayidakrishi farming practices, organic farming principles, techniques, and crop-specific modules.',
          icon: 'courses',
          details: 'Our comprehensive online learning platform includes PowerPoint presentations, expert lectures, and interactive assessments to enrich your learning experience.\n\nContact us for detailed information on course packages and schedules.',
          link: '/online-courses'
        },
        {
          id: 2,
          title: 'Classes/Workshops/Speeches',
          description: 'We offer both in-person and virtual workshops, training sessions, and speaking engagements on sustainable farming practices.',
          icon: 'workshops',
          details: 'Tailored for farmers, government agencies, educational institutions, and agriculture enthusiasts, our programs include hands-on training and workshops in organic agriculture.\n\nContact us to schedule a session tailored to your needs.',
          link: '/workshops'
        },
        {
          id: 3,
          title: 'Project Consultancy',
          description: 'We provide professional consultancy services for agroecology-based projects, covering every stage from proposal development to implementation.',
          icon: 'consultancy',
          details: 'Our services include:\n\n• Project proposal preparation – drafting clear, well-structured proposals.\n• Implementation strategies & monthly planning – designing practical, actionable roadmaps.\n• Individual consultancy – personalized guidance for your specific project needs.\n• Coordination & farm planning – strategic support to align activities with sustainable farm plans.\n\nWith our professional guidance, turn your ideas into practical, sustainable outcomes. Contact us to discuss your project requirements.',
          link: '/consultancy'
        },
        {
          id: 4,
          title: 'Organic Farm Educational Tours in Kerala',
          description: 'We offer guided educational tours to our organic farm in Shoranur, along with visits to nearby organic farms. For those interested in exploring spice cultivation, we also arrange farm visits in the rural areas of Idukki District, which can be combined with refreshing forest treks.',
          icon: 'farm',
          details: 'During these visits, you will gain hands-on learning experiences and practical demonstrations directly from experienced organic farmers.\n\nContact us to book your visit and get details on available tour packages and dates.',
          link: '/farm-visits'
        }
      ],
      advertiseTitle: 'Advertise with Us',
      advertiseDescription: 'Partner with us to reach the agricultural community and promote your products or services.',
      advertiseBenefits: [
        {
          id: 1,
          title: 'Wide and Focused Reach',
          description: 'Purayidakrishi website and blog are free to access with no subscription fees. With our strong local, national, and international network and active social media presence, your products and services will reach a global audience genuinely interested in organic agriculture.'
        },
        {
          id: 2,
          title: 'Targeted Consumers',
          description: 'Your products and services will reach farmers, consumers, researchers, students, NGOs, and agripreneurs who are actively engaged in sustainable and organic farming practices.'
        },
        {
          id: 3,
          title: 'Brand Storytelling',
          description: 'We can feature a dedicated blog article about your products or services, highlighting your authenticity, values, and contribution to organic farming. Stories can be published in both Malayalam and English, making your brand accessible to regional and global readers.'
        },
        {
          id: 4,
          title: 'Website Linking',
          description: 'If you already have a website, we will provide a direct link so readers can visit and purchase from your site instantly. This boosts your website traffic, search ranking, and customer engagement.'
        },
        {
          id: 5,
          title: 'Support Without a Website',
          description: 'Even if you don\'t have a website, we will help showcase your products or services through sponsored content, featured articles, and advertisements. This creates an online presence for you in the global web space.'
        },
        {
          id: 6,
          title: 'Community Trust & Credibility',
          description: 'Our readers trust us for genuine and authentic content. Advertising with us helps you build credibility and brand recognition within the organic agriculture community.'
        },
        {
          id: 7,
          title: 'Cost-Effective & Flexible Plans',
          description: 'We offer different tariff options based on your budget and needs. You can choose from banner ads, sponsored blogs with featured stories, or other partnerships.'
        },
        {
          id: 8,
          title: 'Networking & Collaboration',
          description: 'Advertising here also opens doors to networking opportunities with other organic farmers, entrepreneurs, eco-friendly brands, NGOs, and researchers working towards sustainable agriculture.'
        },
        {
          id: 9,
          title: 'Long-Term Visibility',
          description: 'Unlike traditional ads, blog posts and featured stories remain online and continue to bring attention to your brand for a long time.'
        },
        {
          id: 10,
          title: 'Opportunities in Trade Fairs & Events',
          description: 'Once you advertise with us, we can connect you to local, national, and international organic trade fairs and exhibitions. We will keep you updated about upcoming events where you can participate, network, and showcase your products and services directly to potential buyers and partners.'
        }
      ],
      advertiseFooter: 'To know more about advertisement tariffs and packages, please contact us directly.',
      formTitle: 'Request Consultation',
      formLabels: {
        name: 'Name *',
        email: 'Email *',
        subject: 'Subject *',
        message: 'Message *'
      },
      submitBtn: 'Send Request',
      contactInfo: {
        email: 'info@purayidakrishi.com',
        phone: '+91 98765 43210'
      }
    },
    ml: {
      heading: 'ഞങ്ങളുടെ സേവനങ്ങൾ',
      subheading: 'സമഗ്ര കാർഷിക വിദ്യാഭ്യാസവും കൺസൾട്ടേഷൻ സേവനങ്ങളും',
      services: [
        {
          id: 1,
          title: 'ഓൺലൈൻ കോഴ്സുകൾ',
          description: 'സുസ്ഥിര കൃഷി, ജൈവ കൃഷി രീതികൾ, കാർഷിക പരിസ്ഥിതി തത്വങ്ങൾ എന്നിവയുടെ വിവിധ വശങ്ങൾ ഉൾക്കൊള്ളുന്ന ഇന്ററാക്ടീവ് ഓൺലൈൻ കോഴ്സുകൾ.',
          icon: 'courses',
          details: 'വീഡിയോ ലെക്ചറുകൾ, ഡൗൺലോഡ് ചെയ്യാവുന്ന ഉറവിടങ്ങൾ, ഇന്ററാക്ടീവ് മൂല്യനിർണ്ണയങ്ങൾ എന്നിവയുള്ള സമഗ്ര ഓൺലൈൻ ലേണിംഗ് പ്ലാറ്റ്ഫോം.',
          link: '/online-courses'
        },
        {
          id: 2,
          title: 'ക്ലാസുകൾ/വർക്ക്ഷോപ്പുകൾ/പ്രഭാഷണങ്ങൾ',
          description: 'സുസ്ഥിര കൃഷി രീതികളെക്കുറിച്ചുള്ള ഇൻ-പേഴ്സണും വെർച്വലും ആയ വർക്ക്ഷോപ്പുകൾ, പരിശീലന ക്ലാസുകൾ, പ്രസംഗ ഇടപെടലുകൾ.',
          icon: 'workshops',
          details: 'കർഷകർക്കും കാർഷിക താൽപ്പര്യമുള്ളവർക്കുമായുള്ള പ്രായോഗിക പരിശീലന സെഷനുകൾ, വിദ്യാഭ്യാസ വർക്ക്ഷോപ്പുകൾ, പ്രധാന പ്രഭാഷണങ്ങൾ.',
          link: '/workshops'
        },
        {
          id: 3,
          title: 'പ്രോജക്ട് കൺസൾട്ടൻസി',
          description: 'കാർഷിക പ്രോജക്റ്റുകൾ, ഫാം പ്ലാനിംഗ്, സുസ്ഥിര വികസന സംരംഭങ്ങൾ എന്നിവയ്ക്കുള്ള പ്രൊഫഷണൽ കൺസൾട്ടേഷൻ സേവനങ്ങൾ.',
          icon: 'consultancy',
          details: 'പ്രോജക്ട് പ്ലാനിംഗ്, നടപ്പാക്കൽ തന്ത്രങ്ങൾ, സുസ്ഥിര കാർഷിക വികസനം എന്നിവയിൽ വിദഗ്ധ മാർഗനിർദേശം.',
          link: '/consultancy'
        },
        {
          id: 4,
          title: 'ഞങ്ങളുടെ ഫാം സന്ദർശനങ്ങൾ',
          description: 'ശോരണൂരിലെ ഞങ്ങളുടെ ഫാമിന്റെ മാർഗനിർദേശമുള്ള വിദ്യാഭ്യാസ ടൂറുകൾ, പ്രായോഗിക പഠന അനുഭവങ്ങളോടെ.',
          icon: 'farm',
          details: 'സുസ്ഥിര കൃഷി രീതികൾ നേരിട്ട് അനുഭവിക്കാൻ നിങ്ങളുടെ സന്ദർശനം ബുക്ക് ചെയ്യുക. വിവിധ പാക്കേജുകളും തീയതികളും തിരഞ്ഞെടുക്കുക.',
          link: '/farm-visits'
        }
      ],
      advertiseTitle: 'ഞങ്ങളോടൊപ്പം പരസ്യം ചെയ്യുക',
      advertiseDescription: 'കാർഷിക കമ്മ്യൂണിറ്റിയിലെത്താൻ ഞങ്ങളുമായി പങ്കാളിത്തം ചെയ്ത് നിങ്ങളുടെ ഉൽപ്പന്നങ്ങളോ സേവനങ്ങളോ പ്രോത്സാഹിപ്പിക്കുക.',
      formTitle: 'കൺസൾട്ടേഷൻ അഭ്യർത്ഥിക്കുക',
      formLabels: {
        name: 'പേര് *',
        email: 'ഇമെയിൽ *',
        subject: 'വിഷയം *',
        message: 'സന്ദേശം *'
      },
      submitBtn: 'അഭ്യർത്ഥന അയക്കുക',
      contactInfo: {
        email: 'info@purayidakrishi.com',
        phone: '+91 98765 43210'
      }
    }
  };

  return (
    <section id="services" className="services">
      <div className="container">
        <div className="services-header">
          <h2>{page.heading || content[language].heading}</h2>
          <p>{page.sub || content[language].subheading}</p>
        </div>

        <div className="services-grid">
          {(services.length ? services : content[language].services.map((s) => ({ ...s, slug: (s.title || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''), phone: s.phone || null, email: s.email || null }))).map(service => (
            <div key={service.id} className="service-panel">
              <h3>{service.title}</h3>
              <p className="service-description">{service.description}</p>
              <div className="service-contact-badges">
                {service.phone && (
                  <a className="badge tel" href={`tel:${service.phone.replace(/\s+/g,'')}`}>
                    <span className="icon">📞</span>
                    <span>{service.phone}</span>
                  </a>
                )}
                {service.email && (
                  <a className="badge mail" href={`mailto:${service.email}`}>
                    <span className="icon">✉️</span>
                    <span>{service.email}</span>
                  </a>
                )}
              </div>
              <div className="service-actions">
                <button
                  className="service-btn"
                  onClick={() => {
                    if (service.slug) {
                      window.scrollTo(0, 0);
                      window.location.hash = `#/services/${language}/${service.slug}`;
                    }
                  }}
                >
                  {language === 'en' ? 'Learn More' : 'കൂടുതൽ അറിയുക'} →
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="advertise-section">
          <h3>{content[language].advertiseTitle}</h3>
          <p className="advertise-intro">{content[language].advertiseDescription}</p>
          
          {content[language].advertiseBenefits && (
            <div className="benefits-list">
              <h4>Benefits of Advertising Here</h4>
              {content[language].advertiseBenefits.map((benefit) => (
                <div key={benefit.id} className="benefit-item">
                  <h5>{benefit.id}. {benefit.title}</h5>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
          )}
          
          {content[language].advertiseFooter && (
            <p className="advertise-footer"><strong>{content[language].advertiseFooter}</strong></p>
          )}
          
          <div className="contact-info">
            <p><strong>Email:</strong> {content[language].contactInfo.email}</p>
            <p><strong>Phone:</strong> {content[language].contactInfo.phone}</p>
          </div>
        </div>

        <div className="contact-form">
          <h3>{content[language].formTitle}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">{content[language].formLabels.name}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">{content[language].formLabels.email}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="subject">{content[language].formLabels.subject}</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="message">{content[language].formLabels.message}</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>
            <button type="submit" className="submit-btn" disabled={submitting}>{submitting ? (language === 'en' ? 'Sending...' : 'അയയ്ക്കുന്നു...') : content[language].submitBtn}</button>
            {submitMsg && <div style={{ marginTop: 8 }}>{submitMsg}</div>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default Services;
